import { useEffect } from "react";
import { Outlet, useLoaderData, useParams } from "react-router-dom";

import { useTrackEvent } from "@/deal-room/hooks/useTrackEvent";
import { useVisiblePages } from "@/deal-room/hooks/useVisiblePages";
import { RoomLayout } from "@/deal-room/layout";
import { roomPath } from "@/deal-room/room-path";
import type { Room } from "@/deal-room/schema";

// /<type>/:slug shell (deal-room, content-optimization).
//
// Loads the room JSON via the parent loader (revalidated only when :slug
// changes) and renders the persistent header + outlet for the page child.
// The active tab is derived from the URL - :page param if present, otherwise
// the first visible page in the room (hidden pages don't count as the home).
export function RoomShell() {
    const room = useLoaderData() as Room;
    const params = useParams<{ slug: string; page?: string }>();
    const { pages } = useVisiblePages(room);
    const currentPageSlug = params.page ?? pages[0]?.slug ?? "";

    // Record one "accessed" signal per room visit. The room loader only
    // revalidates when :slug changes, so RoomShell stays mounted across
    // in-room page navigation - this fires on mount / slug change, not per
    // page. The server stamps the session email + identity origin onto the
    // event, so the log answers "who opened this room and when".
    const trackEvent = useTrackEvent(params.slug);
    useEffect(() => {
        trackEvent("room_viewed", { page: currentPageSlug }, { key: params.slug });
        // currentPageSlug intentionally excluded - we want one event per room
        // load, not one per page change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.slug, trackEvent]);

    return (
        <RoomLayout
            room={room}
            currentPageSlug={currentPageSlug}
            basePath={roomPath(room.slug, false, room.roomType)}
        >
            <Outlet />
        </RoomLayout>
    );
}
